import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import TabulacaoAPI from 'dashboard/api/fandata/tabulacao';
import TaxonomiaAPI from 'dashboard/api/fandata/taxonomia';

// Tabulação obrigatória (R1.23) e restrita ao encerramento/transferência (T-97), decidido na
// devolutiva de 13/08 e ajustado no T-99, em 20/08.
//
// Tabular = ter os TRÊS eixos da taxonomia oficial na conversa: a CATEGORIA (11 opções), o
// TIPO DE ATENDIMENTO (5) e a CAUSA RAIZ (T-112, pedido do Douglas em 21/08). A IA preenche
// os dois primeiros na triagem, mas isso NÃO dispensa a tela: o pedido do clube é que o
// agente confirme ou corrija em todo encerramento. O que a IA fez é palpite até alguém olhar,
// e é essa confirmação que dá confiança ao relatório.
//
// Categoria e tipo vivem como ETIQUETA (é o que entra em relatório e automação nativos). A
// causa raiz não: são 161 valores na planilha do clube, e o painel de etiquetas da conta
// viraria ilegível. Ela vai para o atributo da conversa, pela API da taxonomia.
//
// As opções são carregadas uma vez por sessão de módulo: são a taxonomia do clube, mudam em
// deploy, não durante um turno de atendimento.
const opcoes = ref({ categorias: [], tipos: [], causas: {} });
let carregando = null;

export function useFandataTabulacao() {
  const { t } = useI18n();
  const { savedLabels, onUpdateLabels } = useConversationLabels();
  const currentChat = useMapGetter('getSelectedChat');

  const garantirOpcoes = async () => {
    if (opcoes.value.categorias.length) return opcoes.value;
    // Uma requisição só, mesmo com vários componentes pedindo ao mesmo tempo (o botão de
    // resolver e o de atribuir montam juntos).
    if (!carregando) {
      carregando = TabulacaoAPI.opcoes()
        .then(({ data }) => {
          opcoes.value = {
            categorias: data.categorias || [],
            tipos: data.tipos || [],
            // Por CATEGORIA: a causa raiz só faz sentido dentro dela, e oferecer as 161 de
            // uma vez seria pedir para o agente procurar agulha no palheiro.
            causas: data.causas || {},
          };
          return opcoes.value;
        })
        .catch(() => opcoes.value)
        .finally(() => {
          carregando = null;
        });
    }
    return carregando;
  };

  const categoriaAtual = computed(() =>
    (savedLabels.value || []).find(l => opcoes.value.categorias.includes(l))
  );
  const tipoAtual = computed(() =>
    (savedLabels.value || []).find(l => opcoes.value.tipos.includes(l))
  );
  // A causa já gravada nesta conversa (reabertura, ou transferência depois de encerrar):
  // o agente confirma o que existe em vez de escolher de novo.
  const causaAtual = computed(
    () =>
      currentChat.value?.custom_attributes?.fandata_causa_raiz ||
      currentChat.value?.additional_attributes?.fandata_causa_raiz ||
      ''
  );
  // ⚠️ A CHAVE DA TAXONOMIA E A ETIQUETA DA TELA NÃO SÃO A MESMA PALAVRA, e a diferença custou
  // caro em 15/10. Medido na conta do clube: de 12 categorias da planilha, TRÊS não casam com a
  // etiqueta que o agente escolhe.
  //
  //   planilha: "Botafogo Store"   "cupom anual"   "carteirinha"
  //   etiqueta: "botafogo-store"   "cupom-anual"   (não existe)
  //
  // Sem casar, a lista de causas vem vazia, o modal entende "esta categoria não tem causa na
  // planilha" e LIBERA o encerramento sem pedir nenhuma. A causa que o Bira tinha palpitado fica
  // na conversa, contradizendo a categoria que o agente acabou de escolher. Foi o relato da
  // Izabela: "a tabulação que a agente inseriu não foi essa, a plataforma não acatou o comando".
  //
  // Comparar sem acento, sem caixa e sem hífen resolve os três casos e qualquer outro que a
  // planilha traga amanhã, sem depender de alguém manter dois cadastros iguais na mão.
  const normalizar = texto =>
    texto
      ?.toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') || '';

  const causasDe = categoria => {
    const mapa = opcoes.value.causas || {};
    if (mapa[categoria]) return mapa[categoria];

    const alvo = normalizar(categoria);
    const chave = Object.keys(mapa).find(k => normalizar(k) === alvo);
    return chave ? mapa[chave] : [];
  };

  /**
   * Grava os dois eixos preservando as demais etiquetas (sentimento, protocolo, criticidade,
   * as que o agente porventura tenha posto). O 'outro' sai junto: ele significava "a IA não
   * soube", e agora um humano soube.
   */
  const aplicarTabulacao = async ({ categoria, tipo, causaRaiz }) => {
    const daTaxonomia = new Set([
      ...opcoes.value.categorias,
      ...opcoes.value.tipos,
      'outro',
    ]);
    const preservadas = (savedLabels.value || []).filter(
      l => !daTaxonomia.has(l)
    );
    await onUpdateLabels([...preservadas, categoria, tipo]);

    // A causa raiz é gravada DEPOIS das etiquetas e sem derrubar o encerramento se falhar:
    // travar o fechamento do atendimento por causa do campo de relatório inverteria a
    // prioridade da operação.
    if (!causaRaiz) return true;
    try {
      await TaxonomiaAPI.gravarCausa(currentChat.value?.id, causaRaiz);
    } catch (e) {
      // Falhar calado foi o que escondeu o problema por dois dias: o agente escolhia a causa,
      // o servidor recusava, e a tela seguia encerrando como se tivesse gravado. O
      // encerramento continua acontecendo, que é a prioridade da operação, mas agora com aviso.
      useAlert(e?.response?.data?.error || t('CONVERSATION.TABULACAO_CAUSA_ERRO'));
      return false;
    }
    return true;
  };

  return {
    opcoes,
    garantirOpcoes,
    categoriaAtual,
    tipoAtual,
    causaAtual,
    causasDe,
    aplicarTabulacao,
  };
}
